import React from 'react';
import { PropTypes } from 'prop-types';
import classNames from 'classnames';
import { FormattedMessage } from 'react-intl';
import styles from './countrySelector-tw-styles';
import { Button } from '../../Generic/Button';
import constants from '../../../constants/messages/pt-BR';

export default function CountrySelector(props) {
  const { countryData, show, showHandler } = props;
  const dialogRef = React.useRef(null);

  React.useEffect(() => {
    if (show && dialogRef.current) {
      // Get all focusable elements in the dialog
      const getFocusableElements = () => {
        return Array.from(dialogRef.current.querySelectorAll(
          'button, [href], input, [tabindex]:not([tabindex="-1"])'
        ));
      };

      const focusableElements = getFocusableElements();
      if (focusableElements.length > 0) {
        focusableElements[0].focus();
      }

      // Handle Escape key to close dialog
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          showHandler();
        }
      };

      // Handle Tab key to trap focus within dialog
      const handleTab = (e) => {
        if (e.key === 'Tab') {
          const focusableElements = getFocusableElements();
          if (focusableElements.length === 0) return;

          const activeElement = document.activeElement;
          const firstElement = focusableElements[0];
          const lastElement = focusableElements[focusableElements.length - 1];

          if (e.shiftKey) {
            // Shift+Tab on first element should go to last element
            if (activeElement === firstElement) {
              e.preventDefault();
              lastElement.focus();
            }
          } else {
            // Tab on last element should go to first element
            if (activeElement === lastElement) {
              e.preventDefault();
              firstElement.focus();
            }
          }
        }
      };

      document.addEventListener('keydown', handleEscape);
      document.addEventListener('keydown', handleTab);

      // Prevent body scroll when dialog is open
      document.body.style.overflow = 'hidden';

      return () => {
        document.removeEventListener('keydown', handleEscape);
        document.removeEventListener('keydown', handleTab);
        document.body.style.overflow = 'unset';
      };
    }
  }, [show, showHandler]);

  return (
    <div
      id="country-selector-dialog"
      className={classNames(
        styles.countryWrapper,
        show ? styles.countryShow : styles.countryHide
      )}
      role="presentation"
    >
      <div
        ref={dialogRef}
        className={classNames(styles.countryContent)}
        role="dialog"
        aria-modal="true"
        aria-labelledby="country-selector-title"
      >
        <div className={styles.countryClose}>
          <Button 
            aria-label={constants.countrySelectorDialogCloseBtnAriaLabel} 
            className={styles.countryCloseBtn} 
            onClick={showHandler}
            title="Fechar seletor de país"
          />
        </div>
        <div className={styles.countryTitleHeader} id="country-selector-title">
          <FormattedMessage
            id="countrySelectorDialogTitle"
            defaultMessage={constants.countrySelectorDialogTitle}
          />
        </div>
        <ul className={styles.countrySectionsWrapper}>
          {countryData &&
            countryData.map((items, index) => {
              //to read count of countries in each region for screen readers, we are using aria-label in header and list. It will read the region name and country count first, then it will read the list of countries. For example: "América Latina, 6 países, Brasil, Chile, Argentina, Colômbia, México, Perú"
              const countryCount = items.linksCollection.items.length;
              return (
                <li key={index} className={styles.countrySections}>
                  <h2
                    className={styles.countryHeader}
                    aria-label={`${items.text}, ${countryCount} países`}
                  >
                    {items.text}
                  </h2>
                  <ul aria-label={`Lista de países em ${items.text}, ${countryCount} itens`}>
                    {items.linksCollection.items.map((item, idx) => {
                      return (
                        <li key={idx}>
                          <a
                        key={index}
                        className={styles.countryText}
                        href={item.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        title={item.tooltip}
                        aria-label={(() => {
                          const map = {
                            '日本': 'Japão',
                            'Ελλάδα': 'Grécia',
                            'Россия': 'Russia',
                            'Polska': 'Poland',
                            'Türkiye': 'Turkey',
                            'Italia': 'Italy',
                            'Portugal': 'Portugal',
                            'Beligie': 'Belgium',
                            'España': 'Spain',
                            'Brasil': 'Brazil',
                            'Chile': 'Chile',
                            'Argentina': 'Argentina',
                            'Colombia': 'Colombia',
                            'México': 'Mexico',
                            'Perú': 'Peru',
                            'Indonesia': 'Indonesia',
                            'India': 'India',
                            'ไทย': 'Thailand',
                            'Australia & New Zealand': 'Australia & New Zealand',
                            'Malaysia': 'Malaysia',
                            'Chinese Mainland - Greater China': 'China',
                            'Philippines': 'Philippines',
                            'United States': 'United States',
                            'Canada (English)': 'Canada (English)',
                            'Canada (Français)': 'Canada (French)',
                            'United Kingdom & Republic of Ireland': 'United Kingdom & Republic of Ireland'
                          };
                          return map[item.linkText] || undefined;
                        })()} 
                      >
                        {item.linkText}
                      </a>
                        </li>
                      );
                    })}
                  </ul>
                </li>
              );
            })}
        </ul>
      </div>
    </div>
  );
}

CountrySelector.propTypes = {
  show: PropTypes.bool,
  countryData: PropTypes.oneOfType([PropTypes.array]).isRequired,
  showHandler: PropTypes.func.isRequired,
};
// TODO(next16): defaultProps removed in React 19 for function components.
// Convert to inline parameter defaults: const CountrySelector = ({prop = val, show = false }) =>
